import { useMemo } from 'react';
import { useCbsGraphMeasurements } from '../../useCbsGraphMeasurements';
import { TextSize } from 'victory';
import { getLegendWidth } from '../Legend';
import { getBarChartProps } from './getBarChartProps';
import {
	chartPadding,
	fontStyle,
	yAxisLabelBlockSize,
} from '../../cbsGraph.config';

const minBarWidth = 1;
const maxBarWidth = 200;

const barChartGroupGutter = 0;
const barChartBarGutter = 4;

const getPadding = ({
	yAxisTickLabelInlineSize,
	legend,
}: {
	yAxisTickLabelInlineSize: number;
	legend: { labels: Array<string>; title: string; hasLegend: boolean };
}) => {
	const legendWidth = getLegendWidth(legend);

	return {
		top: 5,
		left: yAxisTickLabelInlineSize + yAxisLabelBlockSize + chartPadding.inline,
		bottom: chartPadding.block,
		right: legendWidth + chartPadding.inline,
	};
};

export const useBarGraphMeasurements = (
	barGraphProps: ReturnType<typeof getBarChartProps>,
) => {
	const { graphBlockSize } = useCbsGraphMeasurements();

	return useMemo(() => {
		const {
			barGroupCount,
			barPerGroupCount,
			axis: { labels: axisLabels },
			legend,
		} = barGraphProps;

		const { width: yAxisTickLabelInlineSize } = TextSize.approximateTextSize(
			axisLabels,
			fontStyle,
		);
		const padding = getPadding({
			yAxisTickLabelInlineSize,
			legend,
		});

		const availableChartBlockSize =
			graphBlockSize - padding.top - padding.bottom - 20;
		const barGroupGuttersTotal = (barGroupCount - 1) * barChartGroupGutter;
		const barGuttersTotal =
			(barPerGroupCount - 1) * barChartBarGutter * barGroupCount;

		const availableSpace =
			availableChartBlockSize - barGroupGuttersTotal - barGuttersTotal;

		const barCountTotal = barGroupCount * barPerGroupCount;

		const rawBarWidth = Math.floor(availableSpace / barCountTotal);

		const barInlineSize = Math.min(
			maxBarWidth,
			Math.max(minBarWidth, rawBarWidth),
		);
		const barOffset = barInlineSize + barChartBarGutter;

		const xDomainPadding =
			(barOffset * barPerGroupCount) / 2 + barChartGroupGutter;

		return {
			barInlineSize,
			barOffset,
			xDomainPadding,
			padding,
			yAxisTickLabelInlineSize,
		};
	}, [barGraphProps, graphBlockSize]);
};
